import {
  createMutationKeys,
  createQueryKeys,
} from '@lukemorales/query-key-factory'
import { RequestTypes } from './requestTypes'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { ITemplateEditorChecklistTemplateVersion } from '@core/entities/checklist/TemplateEditorChecklistTemplateVersion'
import { ITemplateEditorChecklistTemplate } from '@core/entities/checklist/TemplateEditorChecklistTemplate/ITemplateEditorChecklistTemplate'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { PageResponse } from '@core/entities/pagination'
import {
  uploadTemplateAnswerFiles,
  uploadTemplateApprovalAnswerFiles,
} from '@core/react-query/features/permits/permit/helpers'
import { headersWithAccessToken } from '@core/utils/api-axios-instance'

export const checklistTemplateKeys = createQueryKeys('checklist-templates', {
  availableTemplates: ({
    projectId,
    filterCreatable,
    registerItemId,
    isUsedForInspections,
  }: RequestTypes['getAvailableTemplates']) => {
    return {
      queryKey: [
        'v2',
        projectId,
        filterCreatable,
        registerItemId,
        isUsedForInspections,
      ],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `checklists/templates/create-list`,
          {
            params: {
              projectId,
              filterCreatable,
              registerItemId,
              isUsedForInspections,
            },
          },
        )
        return data
      },
    }
  },
  getVersions: (payload: RequestTypes['getChecklistTemplateVersions']) => {
    return {
      queryKey: [payload.templateId, payload.page, payload.size],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          page: payload.page,
          size: payload.size,
        })
        const { data } = await APIAxiosInstance.get(
          `checklists/admin/templates/${payload.templateId}/versions${queryString}`,
        )
        return data as PageResponse<ITemplateEditorChecklistTemplateVersion>
      },
    }
  },
  getVersion: (payload: RequestTypes['getChecklistTemplateVersion']) => {
    return {
      queryKey: [payload.templateVersionId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `checklists/admin/templates/${payload.templateId}/versions/${payload.templateVersionId}`,
        )
        return data as ITemplateEditorChecklistTemplateVersion
      },
    }
  },
  getProjectVersion: ({
    projectId,
    templateId,
  }: RequestTypes['getProjectChecklistTemplateVersion']) => {
    return {
      queryKey: [projectId, templateId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `projects/${projectId}/checklists/templates/${templateId}/versions`,
          {},
        )

        return data
      },
    }
  },
  getTemplate: (payload: RequestTypes['getChecklistTemplate']) => {
    return {
      queryKey: [payload.templateId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `checklists/admin/templates/${payload.templateId}`,
        )
        return data as ITemplateEditorChecklistTemplate
      },
    }
  },
  getTemplates: ({
    organisationId,
    projectIds,
    status,
    page,
    size,
    excludeExistingProjectAssignments,
    term,
    mappingLevel,
    isUsedForInspections,
  }: RequestTypes['getChecklistTemplates']) => {
    return {
      queryKey: [
        organisationId,
        projectIds,
        status,
        page,
        size,
        excludeExistingProjectAssignments,
        term,
        mappingLevel,
        isUsedForInspections,
      ],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          organisationId,
          projectIds,
          page,
          size,
          status,
          excludeExistingProjectAssignments,
          term,
          mappingLevel,
          isUsedForInspections,
        })
        const { data } = await APIAxiosInstance.get(
          `checklists/admin/templates${queryString}`,
        )
        return data as PageResponse<ITemplateEditorChecklistTemplate>
      },
    }
  },
  getRoles: (payload: RequestTypes['getChecklistTemplateRoles']) => {
    return {
      queryKey: [payload.templateVersionId],
      queryFn: async () => {
        const headers = headersWithAccessToken(payload)

        const { data } = await APIAxiosInstance.get(
          `checklists/templates/${payload.templateId}/versions/${payload.templateVersionId}/roles`,
          { headers: headers },
        )

        return data.roles
      },
    }
  },
  getChecklistTemplatesReport: ({
    reportingType,
    organisationId,
  }: RequestTypes['getChecklistTemplatesReport']) => {
    return {
      queryKey: [reportingType, organisationId],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          organisationId,
        })
        const { data } = await APIAxiosInstance.get(
          `reporting/templates/checklists/${reportingType}${queryString}`,
        )

        return data
      },
    }
  },
  getProjectApprovalsAssignments: ({
    organisationId,
    projectId,
    templateId,
    templateVersionId,
  }: RequestTypes['getProjectChecklistApprovalsAssignments']) => ({
    queryKey: [organisationId, projectId, templateId, templateVersionId],
    queryFn: async () => {
      const queryString = createQueryKeyString({
        organisationId,
        projectId,
        templateId,
        templateVersionId,
      })

      const { data } = await APIAxiosInstance.get(
        `groups/permissions/checklists/approvals${queryString}`,
      )
      return data
    },
  }),
})

export const checklistTemplateEditorMutationKeys = createMutationKeys(
  'checklist-templates',
  {
    updateSettings: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['updateChecklistTemplateVersionSettings'],
      ) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.templateId}/versions/${payload.templateVersionId}`,
          {
            ...payload,
          },
        )
        return data
      },
    },
    updateQuestions: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['updateChecklistTemplateVersionQuestions'],
      ) => {
        const uploadedQuestions = await uploadTemplateAnswerFiles({
          templateId: payload.templateId,
          answers: payload.checklistQuestions.questions,
        })

        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.templateId}/versions/${payload.templateVersionId}/questions`,
          {
            ...payload,
            checklistQuestions: {
              ...payload.checklistQuestions,
              questions: uploadedQuestions,
            },
          },
        )
        return data
      },
    },
    updateApprovals: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['updateChecklistTemplateVersionApprovals'],
      ) => {
        const approvals = await uploadTemplateApprovalAnswerFiles({
          approvals: payload.approvals,
          templateId: payload.templateId,
        })

        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.templateId}/versions/${payload.versionId}/approvals`,
          { ...payload, approvals },
        )
        return data
      },
    },
    createVersion: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['createChecklistTemplateVersion'],
      ) => {
        const { data } = await APIAxiosInstance.post(
          `checklists/admin/templates/${payload.templateId}/versions`,
          payload,
        )
        return data
      },
    },
    activateTemplateVersion: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['activateChecklistTemplateVersion'],
      ) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.templateId}/activate`,
          payload,
        )
        return data
      },
    },
    discartTemplateVersion: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['activateChecklistTemplateVersion'],
      ) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.templateId}/versions/${payload.versionId}/discard`,
          payload,
        )
        return data
      },
    },
    createTemplateRole: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        templateVersionId,
        ...payload
      }: RequestTypes['createChecklistTemplateRole']) => {
        const data = await APIAxiosInstance.post(
          `checklists/templates/${templateId}/versions/${templateVersionId}/roles`,
          payload,
        )

        return data
      },
    },
    updateTemplateRole: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        templateVersionId,
        roleId,
        ...payload
      }: RequestTypes['updateChecklistTemplateRole']) => {
        const data = await APIAxiosInstance.put(
          `checklists/templates/${templateId}/versions/${templateVersionId}/roles/${roleId}`,
          payload,
        )

        return data
      },
    },
    deleteTemplateRole: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        templateVersionId,
        roleId,
      }: RequestTypes['deleteTemplateRole']) => {
        const data = await APIAxiosInstance.delete(
          `checklists/templates/${templateId}/versions/${templateVersionId}/roles/${roleId}`,
        )

        return data
      },
    },
    updateProjectChecklistTemplateVersion: {
      mutationKey: null,
      mutationFn: async ({
        projectId,
        templateId,
        ...rest
      }: RequestTypes['updateProjectChecklistTemplateVersion']) => {
        const { data } = await APIAxiosInstance.post(
          `projects/${projectId}/checklists/templates/${templateId}/versions`,
          {
            ...rest,
          },
        )

        return data
      },
    },
    createChecklistTemplate: {
      mutationKey: null,
      mutationFn: async (payload: RequestTypes['createChecklistTemplate']) => {
        const { data } = await APIAxiosInstance.post(
          `checklists/admin/templates?organisationId=${payload.organisationId}`,
          {
            ...payload,
          },
        )

        return data
      },
    },
    updateChecklistTemplate: {
      mutationKey: null,
      mutationFn: async (payload: RequestTypes['updateChecklistTemplate']) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.id}`,
          {
            ...payload,
          },
        )

        return data
      },
    },
    archiveChecklistTemplate: {
      mutationKey: null,
      mutationFn: async (payload: RequestTypes['archiveChecklistTemplate']) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.id}/archive`,
        )

        return data
      },
    },
    unArchiveChecklistTemplate: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['unarchiveChecklistTemplate'],
      ) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.id}/unarchive`,
        )

        return data
      },
    },
    assignProjectsToTemplate: {
      mutationKey: null,
      mutationFn: async ({
        projectIds,
        templateId,
      }: RequestTypes['assignProjectsToTemplate']) => {
        const { data } = await APIAxiosInstance.post(
          `checklists/admin/templates/${templateId}/projects`,
          { projectIds },
        )
        return data
      },
    },
    removeProjectsFromTemplate: {
      mutationKey: null,
      mutationFn: async ({
        projectIds,
        templateId,
      }: RequestTypes['removeProjectsFromTemplate']) => {
        await APIAxiosInstance.delete(
          `checklists/admin/templates/${templateId}/projects`,
          {
            data: {
              projectIds,
            },
          },
        )
      },
    },
    updateGlobalChecklistTemplateVersionWarning: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['updateGlobalChecklistTemplateVersionWarning'],
      ) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${payload.templateId}/versions/${payload.versionId}/global-warnings`,
          payload,
        )
        return data
      },
    },
    updateChecklistTemplateVersionWarning: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        versionId,
        ...warning
      }: RequestTypes['updateChecklistTemplateVersionWarning']) => {
        const { data } = await APIAxiosInstance.put(
          `checklists/admin/templates/${templateId}/versions/${versionId}/question-warnings`,
          warning,
        )
        return data
      },
    },
    deleteChecklistTemplateVersionWarning: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        versionId,
        warningId,
        stage,
      }: RequestTypes['deleteChecklistTemplateVersionWarning']) => {
        const { data } = await APIAxiosInstance.delete(
          `checklists/admin/templates/${templateId}/versions/${versionId}/question-warnings/${warningId}?stage=${stage}`,
        )
        return data
      },
    },
    updateProjectChecklistTemplateVersionNotifications: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['updateProjectChecklistTemplateVersionNotifications'],
      ) => {
        const { data } = await APIAxiosInstance.put(
          `projects/${payload.projectId}/checklists/templates/${payload.templateId}/versions/notifications`,
          payload,
        )
        return data
      },
    },
    deleteProjectChecklistTemplateVersionNotification: {
      mutationKey: null,
      mutationFn: async ({
        projectId,
        templateId,
        notificationId,
      }: RequestTypes['deleteProjectChecklistTemplateVersionNotification']) => {
        const { data } = await APIAxiosInstance.delete(
          `projects/${projectId}/checklists/templates/${templateId}/versions/notifications/${notificationId}`,
        )
        return data
      },
    },
    updateProjectApprovalsAssignments: {
      mutationKey: null,
      mutationFn: async (
        payload: RequestTypes['updateProjectChecklistApprovalsAssignments'],
      ) => {
        const { data } = await APIAxiosInstance.post(
          `groups/permissions/checklists/approvals`,
          payload,
        )
        return data
      },
    },
  },
)
